/* eslint-disable indent */

import {
  isMMP,
  isDaXiang,
  isMeiTuan,
  isDianPing,
  isMTYXApp,
  isWaiMaiApp,
  isWaiMaiMerchantApp,
  isMerchant,
  isWXMiniProgram,
} from '@cap/utils';
import qs from 'query-string';
import { isBrowser, getWindow, getGlobalThis } from '@cap/ssr-common-utils';
import { getCommonParams, isAbsoluteURL } from '../urlHelper';
import { IS_B_BIZ, isBiz } from '../account/index';

export interface ShareParams {
  url: string;
  title: string;
  desc: string;
  content: string;
  image?: string;
  channel?: Array<number> | number;
  cid?: string;
  success?: () => void;
}
export interface OpenPageOptions {
  direct?: boolean;
  anime?: string;
  windowOpen?: boolean;
  replace?: boolean;
  source?: string;
  action?: string;
  appId?: string;
  query?: Record<string, string>;
  isCloseCurrentWebview?: boolean;
}

export interface MiniAppShareInfo {
  tabChange?: boolean;
  moduleMenuCode?: number;
  title?: string;
  imageUrl?: string;
  [key: string]: any;
}

/**
 * @deprecated
 * 打开页面操作方式，兼容小程序的打开方式
 */
export const OPEN_PAGE_ACTION = {
  MMP_TO_TITANS: 'MMP_TO_TITANS',
  TITANS_TO_MMP: 'TITANS_TO_MMP',
  WXWPP_SWITCHTAB: 'WXWPP_SWITCHTAB',
  MINIAPP_NAVIGATETO: 'MINIAPP_NAVIGATETO',
};

/**
 * 美团系App与美团小程序宿主App映射
 */
export const MT_APP_WITH_MMP = {
  /**
   * 点评
   */
  DIAN_PING: 'Nova',
  /**
   * 美团优选
   */
  YOU_XUAN: 'meituanyouxuan_app',
  /**
   * 外卖
   */
  WAI_MAI: 'waimai',
  /**
   * 美团
   */
  MEI_TUAN: 'group',
  /**
   * 开店宝
   */
  MERCHANT: 'merchant',
  /**
   * 大象
   */
  DA_XIANG: 'xm',
  /**
   * 外卖商家
   */
  WAI_MAI_MERCHANT: 'waimai_e',
};

/**
 * 获取宿主app
 */

// 协议头地址 http://mmp.sankuai.com/dev/manager/apps
export const MMP_APP_SCHEMA = {
  [MT_APP_WITH_MMP.DIAN_PING]: 'dianping://',
  [MT_APP_WITH_MMP.YOU_XUAN]: 'igrocery://www.grocery.com/',
  [MT_APP_WITH_MMP.WAI_MAI]: 'meituanwaimai://waimai.meituan.com/',
  [MT_APP_WITH_MMP.MEI_TUAN]: 'imeituan://www.meituan.com/',
  [MT_APP_WITH_MMP.MERCHANT]: 'merchant://e.meituan.com/',
  [MT_APP_WITH_MMP.DA_XIANG]: 'mtdaxiang://www.meituan.com/',
  [MT_APP_WITH_MMP.WAI_MAI_MERCHANT]: 'itakeawaybiz://waimaieapi.meituan.com/',
};

/**
 * 获取宿主App标识
 * @returns
 */
export const getHostApp = (userAgent?: string) => {
  const window = getWindow();
  const ua = userAgent ?? window.navigator.userAgent;
  if (isMMP(ua)) {
    const hostAppMatch = ua.match(
      /miniprogram MMP.+(Nova|meituanyouxuan_app|group|waimai|merchant|xm|waimai_e)/i,
    );
    const hostApp = hostAppMatch ? hostAppMatch[1] : '';
    return hostApp;
  }

  if (isMeiTuan(ua)) {
    return MT_APP_WITH_MMP.MEI_TUAN;
  }
  if (isDianPing(ua)) {
    return MT_APP_WITH_MMP.DIAN_PING;
  }
  if (isWaiMaiApp(ua)) {
    return MT_APP_WITH_MMP.WAI_MAI;
  }
  if (isMTYXApp(ua)) {
    return MT_APP_WITH_MMP.YOU_XUAN;
  }
  if (isWaiMaiMerchantApp(ua)) {
    return MT_APP_WITH_MMP.WAI_MAI_MERCHANT;
  }
  if (isMerchant(ua)) {
    return MT_APP_WITH_MMP.MERCHANT;
  }
  if (isDaXiang(ua)) {
    return MT_APP_WITH_MMP.DA_XIANG;
  }

  return '';
};

/**
 * 将B端链接替换为C端链接
 * @param url
 */
const transferBusinessUrlToCustomerUrl = (url: string) => {
  const targetUrl = url
    .replaceAll('hao123.cap.test.sankuai.com', 'hz.cap.test.sankuai.com')
    .replaceAll('hao123.cap.st.meituan.com', '123.cap.st.meituan.com')
    .replaceAll('hao123.meituan.com', '123.meituan.com');
  return targetUrl;
};

/**
 * 将C端链接替换为B端链接
 * @param url
 */
const transferCustomerUrlToBusinessUrl = (url: string) => {
  const targetUrl = url
    .replaceAll('hz.cap.test.sankuai.com', 'hao123.cap.test.sankuai.com')
    .replaceAll('123.cap.st.meituan.com', 'hao123.cap.st.meituan.com')
    .replaceAll('123.meituan.com', 'hao123.meituan.com');
  return targetUrl;
};

/**
 * 根据端类型转换URL
 * @returns
 */
export const transferUrl = (url: string) => {
  const that = getGlobalThis();
  if (!isAbsoluteURL(url)) return url; // 非绝对URL，不进行替换
  if (!IS_B_BIZ(that.runtime_env?.accountType)) return transferBusinessUrlToCustomerUrl(url); // 替换为C端链接
  if (isBiz(url)) return url; // 已为B端链接，不进行替换
  return transferCustomerUrlToBusinessUrl(url); // 替换为B端链接
};

/**
 * 附加通用参数，生成search字符传。
 * @param query
 * @returns
 */
export function queryStringify(query: Record<string, any> = {}) {
  return qs.stringify({
    ...getCommonParams(),
    ...query,
  });
}

/**
 * 附加通用参数，生成url字符传。
 * @param query
 * @returns
 */
export function queryStringifyUrl(
  url: string,
  query: Record<string, any> = {},
  fragmentIdentifier?: string,
) {
  return qs.stringifyUrl({
    url,
    query: {
      ...getCommonParams(),
      ...query,
    },
    fragmentIdentifier,
  });
}

export function setTitle(title: string) {
  if (isBrowser) {
    document.title = title;
  }
}

export const KNB_SHARE_CHANNEL = {
  WECHAT_FRIENDS: 'WECHAT_FRIENDS',
  WECHAT_TIMELINE: 'WECHAT_TIMELINE',
};

/**
 * 设置小程序分享参数
 * @param shareInfo
 */
export async function setMiniAppShareParams(shareInfo: MiniAppShareInfo = {}) {
  if (!isBrowser) {
    return;
  }
  const { userAgent } = navigator;
  if (isMMP(userAgent) || isWXMiniProgram(userAgent)) {
    if (window.wx) {
      window.wx.miniProgram.postMessage({
        data: {
          share: true,
          ...shareInfo,
        },
      });
    }
  }
}
